import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Trophy, Award, GraduationCap, Star } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BADGE_IMAGES = {
  shorts: '/badge_shorts.png',
  basicos: '/badge_basicos.png',
  avanzados: '/badge_avanzados.png',
  psicotrading: '/badge_psicotrading.png',
  algoritmos: '/badge_algoritmos.png',
};

const RANK_STYLES = [
  'bg-yellow-500/20 border-yellow-500/40 text-yellow-400',
  'bg-gray-400/20 border-gray-400/40 text-gray-300',
  'bg-orange-600/20 border-orange-600/40 text-orange-400',
];

export function Leaderboard() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/leaderboard`).then(res => {
      setData(res.data);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="glass-panel rounded-lg p-8 text-center">
        <div className="inline-block h-6 w-6 animate-spin rounded-full border-4 border-solid border-emerald-500 border-r-transparent" />
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="glass-panel rounded-lg p-8 text-center">
        <Trophy className="h-12 w-12 text-emerald-500/30 mx-auto mb-3" />
        <p className="text-emerald-100/50">Aún no hay estudiantes en el ranking</p>
      </div>
    );
  }

  return (
    <div className="glass-panel rounded-lg overflow-hidden" data-testid="leaderboard">
      <div className="p-4 border-b border-emerald-500/20 flex items-center space-x-2">
        <Trophy className="h-5 w-5 text-yellow-400" />
        <h3 className="font-bold text-emerald-50" style={{ fontFamily: 'Rajdhani, sans-serif' }}>RANKING DE ESTUDIANTES</h3>
      </div>
      <div className="divide-y divide-emerald-500/10">
        {data.map((student, idx) => (
          <div
            key={idx}
            className={`flex items-center px-4 py-3 ${idx < 3 ? 'bg-black/20' : ''}`}
            data-testid={`leaderboard-row-${idx}`}
          >
            {/* Rank */}
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold mr-3 flex-shrink-0 border ${
              idx < 3 ? RANK_STYLES[idx] : 'bg-black/30 border-emerald-500/20 text-emerald-100/40'
            }`}>
              {idx === 0 ? <Star className="h-4 w-4" /> : idx + 1}
            </div>

            {/* Name + status */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center space-x-2">
                <p className="text-emerald-50 font-bold text-sm truncate">{student.name}</p>
                {student.certified && (
                  <GraduationCap className="h-3.5 w-3.5 text-green-400 flex-shrink-0" title="Certificado" />
                )}
              </div>
              <p className="text-emerald-100/40 text-[10px]">
                {student.videos_watched} videos
                {student.exam_score ? ` · Examen: ${student.exam_score}%` : ''}
              </p>
            </div>

            {/* Badges */}
            <div className="flex items-center space-x-0.5 flex-shrink-0 ml-2">
              {student.badges.map(b => (
                <img key={b} src={BADGE_IMAGES[b]} alt={b} className="w-7 h-7 rounded-full object-cover ring-1 ring-emerald-500/20" />
              ))}
              {student.badge_count === 0 && (
                <span className="text-emerald-100/20 text-[10px]">Sin insignias</span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
